import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
import { appConfig } from '@/config/app-config';
export interface Contact {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  company_type: string | null;
  company_contact_type: string | null;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
// Interfaz para opciones de filtro (usado en dropdowns)
export interface ContactFilterOption {
  value: string;
  label: string;
  email?: string;
  company_type?: string;
  avatar_url?: string;
}
// Tipos de respuesta
interface ContactsResponse {
  data: Contact[];
  totalCount: number;
}
interface ContactsState extends FilteredState<Contact> {
  selectedContact: Contact | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  // Estado para opciones de filtro (cacheadas para evitar recargas)
  filterOptions: ContactFilterOption[];
  filterOptionsLoading: boolean;
  filterOptionsError: string | null;
  filterOptionsLastFetch: number | null;
}
const initialState: ContactsState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'name',
  sortDirection: 'asc',
  searchTerm: null,
  selectedContact: null,
  creating: false,
  updating: false,
  deleting: false,
  // Estado para opciones de filtro
  filterOptions: [],
  filterOptionsLoading: false,
  filterOptionsError: null,
  filterOptionsLastFetch: null,
};
// Thunk para cargar contacts con paginación y filtros (para vistas de lista)
export const fetchContacts = createAsyncThunk<ContactsResponse, any>('contacts/fetchContacts', async (params) => {
  throw new Error('Not implemented yet');
});
// Thunk para cargar opciones de filtro (solo id, nombre, email) - CACHEADO
export const fetchContactFilterOptions = createAsyncThunk<ContactFilterOption[], void>(
  'contacts/fetchFilterOptions',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const contactsState = state.contacts as ContactsState;
    // ✅ CACHE: Si ya tenemos datos frescos (menos de 5 minutos), usar cache
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
    if (
      contactsState.filterOptionsLastFetch &&
      Date.now() - contactsState.filterOptionsLastFetch < CACHE_DURATION &&
      contactsState.filterOptions.length > 0
    ) {
      return contactsState.filterOptions;
    }
    try {
      // Obtener token de autenticación
      const token = localStorage.getItem('authToken') || '';
      const url = appConfig.getApiUrl('masters/contacts?startRow=0&endRow=500&sortProperty=name&sortDirection=asc');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      const result = await response.json();
      const contacts = result?.data?.rows || [];
      // Transformar a formato de opciones de filtro
      const filterOptions: ContactFilterOption[] = contacts.map((contact: any) => ({
        value: contact.id.toString(),
        label: contact.name,
        email: contact.email,
        company_type: contact.company_type,
        avatar_url: contact.avatar_url
      }));
      return filterOptions;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error al cargar opciones de filtro');
    }
  }
);
export const fetchContactById = createAsyncThunk<Contact, number>('contacts/fetchContactById', async (id) => {
  throw new Error('Not implemented yet');
});
export const createContact = createAsyncThunk<Contact, Partial<Contact>>('contacts/createContact', async (contact) => {
  throw new Error('Not implemented yet');
});
export const updateContact = createAsyncThunk<{ id: number; data: Contact }, { id: number; data: Partial<Contact> }>('contacts/updateContact', async ({ id, data }) => {
  throw new Error('Not implemented yet');
});
export const deleteContact = createAsyncThunk<number, number>('contacts/deleteContact', async (id) => {
  throw new Error('Not implemented yet');
});
const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectContact: (state, action: PayloadAction<Contact | null>) => {
      state.selectedContact = action.payload;
    },
    clearSelection: (state) => {
      state.selectedContact = null;
    },
  },
  extraReducers: (builder) => {
    // Casos para fetchContacts (lista completa)
    builder
      .addCase(fetchContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar contactos';
      })
      // Casos para fetchContactFilterOptions (opciones cacheadas)
      .addCase(fetchContactFilterOptions.pending, (state) => {
        state.filterOptionsLoading = true;
        state.filterOptionsError = null;
      })
      .addCase(fetchContactFilterOptions.fulfilled, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptions = action.payload;
        state.filterOptionsLastFetch = Date.now();
      })
      .addCase(fetchContactFilterOptions.rejected, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptionsError = action.payload as string || 'Error al cargar opciones de filtro';
      });
  },
});
// Selectores para acceder al estado
export const selectContactFilterOptions = (state: any) => state.contacts.filterOptions;
export const selectContactFilterOptionsLoading = (state: any) => state.contacts.filterOptionsLoading;
export const selectContactFilterOptionsError = (state: any) => state.contacts.filterOptionsError;
// Helper para verificar si las opciones de filtro están frescas
export const areContactFilterOptionsFresh = (state: any): boolean => {
  const lastFetch = state.contacts.filterOptionsLastFetch;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  return lastFetch && Date.now() - lastFetch < CACHE_DURATION;
};
export const { setFilters, clearFilters, setSearchTerm, setSort, setPage, setPageSize, selectContact, clearSelection } = contactsSlice.actions;
export default contactsSlice.reducer;
